import { Box } from "@mui/material";
import { MutableRefObject, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

const RECALCULATE_EVENT = "renderRelativeToAnchor:recalculate";

export default function RenderRelativeToAnchor(props: {
    position: "top left" | "top right" | "bottom left" | "bottom right";
    anchor: MutableRefObject<HTMLElement | null>;
    children: React.ReactNode;
    gap?: {
        top?: number;
        right?: number;
        bottom?: number;
        left?: number;
    };
}) {
    const [anchorRect, setAnchorRect] = useState<{
        top: number;
        right: number;
        left: number;
        bottom: number;
        width: number;
        height: number;
    } | null>(null);

    useEffect(() => {
        const anchorElement = props.anchor?.current;
        if (!anchorElement) {
            return;
        }

        let rafId: number | null = null;

        const runMeasurement = () => {
            const { top, right, bottom, left, width, height } =
                anchorElement.getBoundingClientRect();

            setAnchorRect((prev) => {
                if (
                    prev &&
                    prev.top === top &&
                    prev.right === right &&
                    prev.bottom === bottom &&
                    prev.left === left &&
                    prev.width === width &&
                    prev.height === height
                ) {
                    return prev;
                }

                return { top, right, bottom, left, width, height };
            });
        };

        const scheduleUpdate = (shouldBroadcast: boolean) => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }

            rafId = window.requestAnimationFrame(() => {
                runMeasurement();
                if (shouldBroadcast) {
                    window.dispatchEvent(
                        new CustomEvent(RECALCULATE_EVENT, {
                            detail: anchorElement,
                        })
                    );
                }
            });
        };

        const handleResize = () => scheduleUpdate(true);
        const handleScroll = () => scheduleUpdate(true);
        const handleGlobalRecalc = () => scheduleUpdate(false);

        scheduleUpdate(false);

        window.addEventListener("resize", handleResize);
        window.addEventListener("scroll", handleScroll, true);
        window.addEventListener(RECALCULATE_EVENT, handleGlobalRecalc);

        const resizeObserver =
            typeof ResizeObserver !== "undefined"
                ? new ResizeObserver(() => {
                      scheduleUpdate(true);
                  })
                : null;

        resizeObserver?.observe(anchorElement);

        const intersectionObserver =
            typeof IntersectionObserver !== "undefined"
                ? new IntersectionObserver(
                      () => {
                          scheduleUpdate(true);
                      },
                      {
                          root: null,
                          threshold: [0, 0.5, 1],
                      }
                  )
                : null;

        intersectionObserver?.observe(anchorElement);

        return () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("scroll", handleScroll, true);
            window.removeEventListener(RECALCULATE_EVENT, handleGlobalRecalc);
            resizeObserver?.disconnect();
            intersectionObserver?.disconnect();
        };
    }, [props.anchor]);

    const styles = useMemo(() => {
        if (!anchorRect) {
            return { visibility: "hidden" } as const;
        }

        switch (props.position) {
            case "top left":
                return {
                    top: anchorRect.top - (props.gap?.top ?? 0),
                    right: anchorRect.left - (props.gap?.left ?? 0),
                    transform: "translate(100%,100%)",
                } as const;
            case "top right":
                return {
                    top: anchorRect.top - (props.gap?.top ?? 0),
                    left: anchorRect.right - (props.gap?.right ?? 0),
                    transform: "translate(-100%, +100%)",
                } as const;
            case "bottom right":
                return {
                    top: anchorRect.bottom + (props.gap?.bottom ?? 0),
                    left: anchorRect.right - (props.gap?.right ?? 0),
                    transform: "translateX(-100%)",
                } as const;
            case "bottom left":
                return {
                    top: anchorRect.bottom - (props.gap?.bottom ?? 0),
                    right: anchorRect.left - (props.gap?.right ?? 0),
                    transform: "translate(100%,-100%)",
                } as const;
            default:
                return {
                    top: anchorRect.bottom + (props.gap?.bottom ?? 0),
                    left: anchorRect.left - (props.gap?.left ?? 0),
                } as const;
        }
    }, [anchorRect, props.position]);

    return createPortal(
        <Box
            sx={{
                position: "fixed",
                minWidth: "20px",
                minHeight: "20px",
                width: "fit-content",
                height: "fit-content",
                ...styles,
            }}
        >
            {props.children}
        </Box>,
        document.body
    );
}
